import { Component, OnInit, Inject, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Row } from '../../../../../../shared/payments/confirmation-details-table/table-row.api';
import { PayPlanFlow } from '../../../automatic-payments/pay-plan/shared/pay-plan-flow.service';
import { PayPlanAdapter } from '../../../automatic-payments/pay-plan/shared/pay-plan.adapter';
import { environment } from '../../../../../../../environments/environment';
import { ISBRouteParams } from '../../../../../../shared/routing/isb-route.model';
import { SessionService } from '../../../../../../shared/session/session.service';
import { UpComingPaymentModel } from './up-coming-payments.model';
import { BasePageComponent } from '../../../../../../shared/base/base-page.component';
import { PageName } from '../../../../../../shared/page-name.enum';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { EasternTimeDatePipe } from '@nationwide/internet-servicing-angular-pipes';
import { HttpCachingService } from '../../../../../../shared/service/http-caching-service/http-caching.service';
import { CelebrusTrackingService } from '../../../../../../shared/service/celebrus-tracking/celebrus-tracking.service';
import { FocusOnInitDirective } from '../../../../../../shared/attr-directives/focus-on-init.directive';
import { PaymentSubHeaderComponent } from '../../../../../../shared/payments/payment-sub-header/payment-sub-header.component';
import { ConfirmationDetailsTableComponent } from '../../../../../../shared/payments/confirmation-details-table/confirmation-details-table.component';
import { ButtonWrapperComponent } from '@nationwide/internet-servicing-angular-components';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'edit-payment-plan-confirmation',
    templateUrl: './edit-payment-plan-confirmation.component.html',
    imports: [FocusOnInitDirective, PaymentSubHeaderComponent, ConfirmationDetailsTableComponent, ButtonWrapperComponent, DatePipe]
})
export class EditPaymentPlanConfirmationComponent extends BasePageComponent implements OnInit, OnDestroy {
    isPayplanUpdateSuccess = signal(false);
    confirmationDetails: Row[];
    upComingPaymentModel: UpComingPaymentModel;
    pageName = PageName.EDIT_PAYMENT_PLAN_CONFIRMATION;

    constructor(
        protected router: Router,
        private payPlanFlow: PayPlanFlow,
        private easternTimeDatePipe: EasternTimeDatePipe,
        private payPlanAdapter: PayPlanAdapter,
        private httpCachingService: HttpCachingService,
        private readonly celebrusTrackingService: CelebrusTrackingService,
        @Inject('logger') private logger: LoggerService,
        @Inject('window') private window: Window,
        protected session: SessionService) {
        super(session, router);
    }

    ngOnInit(): void {
        if (!this.payPlanFlow.flowInPlay) {
            this.cancelEditPaymentPlan();
        }
        this.session.currentPage = this.pageName;
        this.isPayplanUpdateSuccess.set(this.payPlanFlow.isPayPlanUpdateSuccess);
        this.initializeConfirmationDetails();
        this.httpCachingService.clearCache();
    }

    ngOnDestroy(): void {
        this.celebrusTrackingService.clearCelebrusSessionStorage();
    }

    currentRouteParams(): string {
        return this.router.url.split('?')[1];
    }

    cancelEditPaymentPlan(): void {
        this.router.navigateByUrl(
            environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.paymentPreferences(
                this.currentRouteParams()
            )
        );
    }

    initializeConfirmationDetails(): void {
        this.confirmationDetails = [
            { label: 'Account number:', value: this.payPlanFlow.billingAccountNumber },
            { label: 'Payment Plan:', value: this.payPlanFlow.selectedPayPlan },
            { label: 'Effective date', value: this.easternTimeDatePipe.transform(this.payPlanFlow.payPlanChangeDate, 'MM/dd/yyyy') },
            { label: 'Policy type', value: this.payPlanFlow.policyType }
        ];
        this.buildUpComingPaymentsSection();
    }

    buildUpComingPaymentsSection(): void {
        this.upComingPaymentModel = this.payPlanAdapter.mapUpComingPayments();
    }

    get payPlan(): string {
        return this.payPlanFlow.selectedPayPlan;
    }

    get canPayNow(): boolean {
        return this.payPlanFlow.canPayNow;
    }

    get contactusUrl(): string {
        return environment.CONTACT_US.url;
    }

    navigateToMakeAPayment(): void {
        this.logger.info('On click of Edit Payment Plan Make a Payment link');
        this.router.navigateByUrl(environment.ISB.ENDPOINTS.RELATIVE.payBillLanding(this.urlParams));
    }

    showPrintDialog(): void {
        this.window.print();
    }

    get billLabel(): string {
        return this.upComingPaymentModel?.headerDetails?.billLabel;
    }

    get amountLabel(): string {
        return this.upComingPaymentModel?.headerDetails?.amountLabel;
    }

    get dateLabel(): string {
        return this.upComingPaymentModel?.headerDetails?.dateLabel;
    }

    get urlParams(): ISBRouteParams {
        return {
            accountNumber: this.session.billingAccountNumber,
            sessionId: this.session.a2aSessionId
        };
    }
}
